import React, { useCallback, useMemo, useState } from "react";
import Markdown from "markdown-to-jsx";
import { useTheme } from "@material-ui/core/styles";
import StartConversationFromNumberModal from "../StartConversationFromNumberModal";
import { linkifyPhoneNumbers } from "../../utils/phoneNumbers";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, href, onPhoneClick, ...props }) => {
	const theme = useTheme();
	const linkColor = theme.palette.type === "dark" ? "#64B5F6" : "#1565C0";

	if (href?.startsWith("tel:")) {
		return (
			<a
				{...props}
				href={href}
				onClick={event => {
					event.preventDefault();
					onPhoneClick(href.slice(4));
				}}
				title="Copiar o iniciar conversación"
				style={{
					cursor: "pointer",
					textDecoration: "underline",
					fontWeight: 600,
					color: linkColor,
				}}
			>
				{children}
			</a>
		);
	}

	return (
		<a
			{...props}
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			style={{ color: linkColor }}
		>
			{children}
		</a>
	);
};

const MarkdownWrapper = ({ children }) => {
	const [selectedPhone, setSelectedPhone] = useState("");
	const boldRegex = /\*(.*?)\*/g;
	const tildaRegex = /~(.*?)~/g;
	const handlePhoneClick = useCallback(phone => setSelectedPhone(phone), []);
	
	if(children && children.includes('BEGIN:VCARD'))
		//children = "Diga olá ao seu novo contato clicando em *conversar*!";
		children = null;
	
	if(children && children.includes('data:image/'))
		children = null;
	
	if (children && boldRegex.test(children)) {
		children = children.replace(boldRegex, "**$1**");
	}
	if (children && tildaRegex.test(children)) {
		children = children.replace(tildaRegex, "~~$1~~");
	}

	children = linkifyPhoneNumbers(children);

	const options = useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: true,
			forceInline: true,
			overrides: {
				a: {
					component: props => (
						<CustomLink {...props} onPhoneClick={handlePhoneClick} />
					),
				},
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, [handlePhoneClick]);

	if (!children) return null;
	
	return (
		<>
			<Markdown options={options}>{children}</Markdown>
			<StartConversationFromNumberModal
				open={Boolean(selectedPhone)}
				initialNumber={selectedPhone}
				onClose={() => setSelectedPhone("")}
			/>
		</>
	);
};

export default MarkdownWrapper;
